// Pay-early dialog (marketplace M4): pay extra toward a loan (choose the
// benefit) or clear it entirely — via a mobile-money prompt to the employee's
// own phone. Quotes are server-computed and shown BEFORE any money moves.
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { creditApi, type PrepayQuote, type PayoffQuote } from '@/features/credit/api/creditApi';
import type { CreditFacility } from '@/features/credit/types';
import { formatMoney } from '../lib/format';
import { getApiErrorMessage } from '@/lib/apiError';
import { PhoneInput } from '@/features/shared/components/PhoneInput';

const NETWORKS = [
  { key: 'mpesa', label: 'M-Pesa' },
  { key: 'tigo', label: 'Mixx by Yas (Tigo)' },
  { key: 'airtel', label: 'Airtel Money' },
  { key: 'halopesa', label: 'HaloPesa' },
  { key: 'azampesa', label: 'AzamPesa' },
  { key: 'crdb', label: 'CRDB Bank' },
  { key: 'nmb', label: 'NMB Bank' },
];
const BANKS = new Set(['crdb', 'nmb']);

export function PayEarlyDialog({ facility, onClose, onStarted }: {
  facility: CreditFacility;
  onClose: () => void;
  onStarted: () => void;
}) {
  const [mode, setMode] = useState<'prepay_reduce_term' | 'prepay_reduce_installment' | 'settle'>('prepay_reduce_term');
  const [amount, setAmount] = useState('');
  const [phone, setPhone] = useState('');
  const [network, setNetwork] = useState('mpesa');
  const [accountNumber, setAccountNumber] = useState('');
  const [bankOtp, setBankOtp] = useState('');
  // The quote AND the inputs it answers: Send is enabled only for a quote of
  // the current amount and mode (credit #19 review — a quote kept across input
  // changes, or a late answer for an earlier amount, enabled Send for an amount
  // that was refused).
  const [quoted, setQuoted] = useState<{ amount: number; mode: string; quote: PrepayQuote } | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const quoteRequest = useRef(0);
  const [payoff, setPayoff] = useState<PayoffQuote | null>(null);
  const [sending, setSending] = useState(false);

  const isSettle = mode === 'settle';
  const amt = Number(amount);

  useEffect(() => {
    creditApi.payoffQuote(facility.id).then(setPayoff).catch(() => setPayoff(null));
  }, [facility.id]);

  // Live prepay quote, debounced. Any input change drops the previous quote at
  // once; only the answer to the LATEST request is kept (a slower, earlier one
  // is discarded); a refusal is shown in the server's words.
  useEffect(() => {
    const request = ++quoteRequest.current;
    setQuoted(null);
    setQuoteError(null);
    if (isSettle || !amt || amt <= 0) return;
    const t = setTimeout(() => {
      creditApi.prepayQuote(facility.id, amt, mode)
        .then((q) => { if (request === quoteRequest.current) setQuoted({ amount: amt, mode, quote: q }); })
        .catch((e) => { if (request === quoteRequest.current) setQuoteError(getApiErrorMessage(e, "This amount can't be paid early.")); });
    }, 350);
    return () => clearTimeout(t);
  }, [facility.id, amt, mode, isSettle]);
  const quote = quoted && quoted.amount === amt && quoted.mode === mode ? quoted.quote : null;

  const submit = async () => {
    setSending(true);
    try {
      const isBank = BANKS.has(network);
      const res = await creditApi.payFacility(facility.id, {
        amount: isSettle ? undefined : amt,
        mode, network,
        phone: isBank ? accountNumber : phone,
        bank_otp: isBank ? bankOtp : undefined,
      });
      toast.success(res.message || (isBank ? 'Your bank payment is being processed.' : 'A payment request was sent to your phone — approve it to complete.'), { duration: 8000 });
      onStarted();
      onClose();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not start the payment.'), { duration: 6000 });
    } finally {
      setSending(false);
    }
  };

  const isBank = BANKS.has(network);
  const canSubmit = (isBank ? accountNumber.trim().length >= 6 && bankOtp.trim().length >= 4 : !!phone)
    && (isSettle ? payoff?.allowed : amt > 0 && !!quote);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-4 p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-foreground">Pay early</h2>
            <button onClick={onClose} aria-label="Close"><X className="h-4 w-4 text-muted-foreground" /></button>
          </div>
          <p className="text-xs text-muted-foreground">
            Outstanding {formatMoney(facility.outstandingBalance, facility.currency)} · {formatMoney(facility.installment, facility.currency)}/mo from payroll.
          </p>

          <div className="grid grid-cols-3 gap-2 text-xs">
            {([
              ['prepay_reduce_term', 'Finish sooner'],
              ['prepay_reduce_installment', 'Pay less monthly'],
              ['settle', 'Pay everything'],
            ] as const).map(([k, label]) => (
              <button
                key={k}
                onClick={() => setMode(k)}
                className={`rounded-lg border px-2 py-2 font-medium ${mode === k ? 'border-orange-400 bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400' : 'border-border text-muted-foreground hover:bg-muted'}`}
              >
                {label}
              </button>
            ))}
          </div>

          {isSettle ? (
            payoff?.allowed ? (
              <div className="rounded-lg bg-muted/50 p-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Payoff amount</span><span className="font-semibold text-foreground">{formatMoney(payoff.payoff_amount, facility.currency)}</span></div>
                {payoff.waived_interest > 0 && (
                  <div className="flex justify-between text-success"><span>Interest waived</span><span>−{formatMoney(payoff.waived_interest, facility.currency)}</span></div>
                )}
                {payoff.fee > 0 && (
                  <div className="flex justify-between text-muted-foreground"><span>Settlement fee</span><span>+{formatMoney(payoff.fee, facility.currency)}</span></div>
                )}
              </div>
            ) : (
              <p className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">This lender doesn't allow early settlement on this product.</p>
            )
          ) : (
            <>
              <div>
                <label className="text-sm font-medium text-foreground" htmlFor="prepay-amount">Extra amount ({facility.currency})</label>
                <input
                  id="prepay-amount" type="number" min="1" value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-orange-400"
                />
              </div>
              {quoteError && (
                <p role="alert" className="rounded-lg bg-danger-soft p-3 text-xs text-danger">{quoteError}</p>
              )}
              {quote && (
                <div className="rounded-lg bg-muted/50 p-3 text-sm">
                  <div className="flex justify-between text-success"><span>Interest you save</span><span>−{formatMoney(quote.interest_saved, facility.currency)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Remaining after</span><span className="font-medium text-foreground">{formatMoney(quote.remaining_after, facility.currency)}</span></div>
                  {mode === 'prepay_reduce_term' ? (
                    <div className="flex justify-between"><span className="text-muted-foreground">New remaining term</span><span className="font-medium text-foreground">{quote.new_remaining_term} months</span></div>
                  ) : (
                    <div className="flex justify-between"><span className="text-muted-foreground">New monthly deduction</span><span className="font-medium text-foreground">{formatMoney(quote.new_installment, facility.currency)}</span></div>
                  )}
                </div>
              )}
            </>
          )}

          <div>
            <label className="text-sm font-medium text-foreground">{isBank ? 'Pay from (bank account)' : 'Pay from (mobile money)'}</label>
            <div className="mt-1 flex gap-2">
              <select value={network} onChange={(e) => setNetwork(e.target.value)}
                className="rounded-lg border border-border bg-background px-2 py-2 text-sm text-foreground outline-none focus:border-orange-400">
                {NETWORKS.map((n) => <option key={n.key} value={n.key}>{n.label}</option>)}
              </select>
              {isBank ? (
                <input
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value.replace(/[^\d]/g, ''))}
                  placeholder="Account number"
                  inputMode="numeric"
                  className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-orange-400"
                />
              ) : (
                <PhoneInput value={phone} onChange={setPhone} className="flex-1" />
              )}
            </div>
            {isBank ? (
              <>
                <input
                  value={bankOtp}
                  onChange={(e) => setBankOtp(e.target.value.replace(/[^\d]/g, '').slice(0, 8))}
                  placeholder="One-time code from your bank"
                  inputMode="numeric"
                  className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm tracking-widest text-foreground outline-none focus:border-orange-400"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Generate a one-time code in your bank's own app ({network === 'crdb' ? 'SimBanking' : 'NMB Mkononi'}) and enter it here to authorize the payment. On-time extra payments also strengthen your KaziScore.
                </p>
              </>
            ) : (
              <p className="mt-1 text-[11px] text-muted-foreground">You'll get a prompt on this phone — approve it with your PIN. On-time extra payments also strengthen your KaziScore.</p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
            <Button onClick={submit} disabled={!canSubmit || sending}>
              {sending ? 'Sending…' : 'Send payment request'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
